import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import { appConfigService } from '@/providers/services/app/app-config';
import { ReportDefaultColor } from '@/utils/constants';

@Component({
    name: 'donut-chart-card',
})
export default class DonutChartCard extends Vue {

    @Prop()
    public dataList?: any;

    @Prop()
    public totalCount?: any;

    public totalActiveUsers: number = 0;

    public chartData: any[] = [];

    public isLoading: boolean = false;

    public get reportUserType() {
        return appConfigService.getReportUserType();
    }

    @Watch('dataList', { deep: true })
    public async onChangeData() {
        this.loadData();
    }

    // -----------------------------------------------------------------
    // Hooks

    public mounted() {
        this.loadData();
    }

    // ------------------------------------------------------------------
    // Methods

    public loadData() {
        this.totalActiveUsers = this.totalCount || 0;
        this.prepareChartData();
        this.loadDonutChart();
    }

    /**
     * Prepares the data for the donut chart.
     */
    public prepareChartData(): void {
        if (!this.dataList || this.dataList.length === 0) {
            this.chartData = [];
            return;
        }
        const totalActiveUsers = this.dataList.reduce(
            (sum: number, item: { total_active_user_count: number }) => sum + item.total_active_user_count,
            0,
        );
        this.chartData = this.dataList.map((item: { user_type: string; total_active_user_count: number }) => {
            const userType = item.user_type;
            const activeUsers = item.total_active_user_count;
            const percentage = (activeUsers / totalActiveUsers * 100);
            const color = this.reportUserType[userType].line || ReportDefaultColor;
            return {
                name: userType, value: activeUsers, color, percentage: Math.round(percentage * 100) / 100 + '%',
            };
        });
    }

    /**
     * Renders the donut chart using D3.js.
     */
    public loadDonutChart() {
        // Remove svg
        d3.select(this.$refs.chart as HTMLElement).select('svg').remove();
        const width = 200;
        const height = 200;
        const radius = Math.min(width, height) / 2;

        const svg = d3
            .select(this.$refs.chart as HTMLElement)
            .append('svg')
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${width / 2}, ${height / 2})`);

        const pie = d3.pie().value((d: any) => d.value);
        const arc: any = d3.arc().innerRadius(92).outerRadius(radius * 0.8);

        const chartData = pie(this.chartData);

        // Append paths for the donut chart
        svg
            .selectAll('path')
            .data(chartData)
            .enter()
            .append('path')
            .attr('d', arc)
            .attr('fill', (d: any) => d.data.color);

        // Add Total Users text in the center
        svg
            .append('text')
            .attr('text-anchor', 'middle')
            .attr('dy', '-0.3em')
            .style('font-size', '16px')
            .style('font-weight', '600')
            .style('fill', '#969696')
            .text('Total Users');

        svg
            .append('text')
            .attr('text-anchor', 'middle')
            .attr('dy', '1.3em')
            .style('font-size', '15px')
            .style('font-weight', '500')
            .style('fill', '#000')
            .style('cursor', this.totalActiveUsers ? 'pointer' : 'default')
            .text(this.totalActiveUsers.toLocaleString())
            .on('click', () => {
                if (this.totalActiveUsers) {
                    this.onTotalUsersClick();
                }
            });
    }

    /**
     * Calculates the percentage of a given value relative to the total.
     */
    public calculatePercentage(count: any) {
        const total = this.chartData.reduce(
            (acc: any, user: any) => acc + user.value,
            0,
        );
        return Math.round((count / total) * 100);
    }

    public onTotalUsersClick() {
        this.$emit('onClickTotalActiveUsers');
    }
}
